import { Permissions, toBitfield, type ChannelDto } from '@lagerfeuer/shared';
import { ArrowDown, ArrowUp, Bell, BellOff, ChevronDown, ChevronRight, FolderInput, FolderPlus, LogOut, Pencil, Plus, Settings, UserPlus, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ConfirmDialog } from '../../components/Modal';
import { Menu, anchorFrom, type Anchor, type MenuItem } from '../../components/Popover';
import { Tooltip } from '../../components/Tooltip';
import { errorMessage } from '../../lib/api';
import { channelsApi, membersApi, notificationsApi } from '../../lib/endpoints';
import { paths, pathForChannel } from '../../lib/routes';
import { useAppStore } from '../../store/app';
import { overlays } from '../../store/overlays';
import {
  can,
  channelPermissions,
  groupServerChannels,
  isMuted,
  isUnread,
  mentionCount,
  serverPermissions,
  displayName,
} from '../../store/selectors';
import { toast, useUi } from '../../store/ui';
import { extraMenuItems } from '../extras';
import { Avatar } from '../../components/Avatar';
import { applyPositions, categoriesOf, parentOf, planDrop, planMove, planStep, siblingsOf, type PositionUpdate } from '../channel-settings/channelOrder';
import '../channel-settings/channel-sort.css';
import { markServerRead } from './markRead';
import { ChannelIcon } from './channelIcons';

const COLLAPSE_KEY = 'lf:collapsed-categories';
function loadCollapsed(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(COLLAPSE_KEY) ?? '[]') as string[]);
  } catch {
    return new Set();
  }
}

/** Kanalliste eines Servers mit Kategorien, Ungelesen-Status, Sprachkanal-Teilnehmern und Sortierung per Drag & Drop. */
export function ServerSidebar({ serverId }: { serverId: string }) {
  const navigate = useNavigate();
  const { channelId: activeId } = useParams();
  const server = useAppStore((s) => s.servers[serverId]);
  const channels = useAppStore((s) => s.channels);
  const roles = useAppStore((s) => s.roles[serverId]);
  const me = useAppStore((s) => s.me);
  const notificationSettings = useAppStore((s) => s.notificationSettings);
  const readStates = useAppStore((s) => s.readStates);
  const groups = useMemo(() => groupServerChannels(useAppStore.getState(), serverId), [channels, roles, server, serverId]); // eslint-disable-line react-hooks/exhaustive-deps
  const perms = useMemo(() => serverPermissions(useAppStore.getState(), serverId), [roles, server, serverId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [collapsed, setCollapsed] = useState(loadCollapsed);
  const [menu, setMenu] = useState<{ anchor: Anchor; items: MenuItem[]; testId: string } | null>(null);
  const [confirmLeave, setConfirmLeave] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; after: boolean } | null>(null);
  const muted = useMemo(() => isMuted(useAppStore.getState(), 'SERVER', serverId), [notificationSettings, serverId]); // eslint-disable-line react-hooks/exhaustive-deps
  void readStates;

  if (!server || !me) return null;
  const canManageChannels = can(perms, 'MANAGE_CHANNELS');
  const canManageServer = (['MANAGE_SERVER', 'MANAGE_ROLES', 'MANAGE_EMOJIS', 'BAN_MEMBERS', 'KICK_MEMBERS', 'MODERATE_MEMBERS', 'MANAGE_NICKNAMES'] as const).some((p) => can(perms, p));
  const isOwner = server.ownerId === me.id;

  const toggleCollapsed = (id: string) => {
    const next = new Set(collapsed);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setCollapsed(next);
    try {
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify([...next]));
    } catch {
      /* egal */
    }
  };

  const serverMenu = (anchor: Anchor) => {
    const extra = extraMenuItems.filter((i) => i.slot === 'serverMenu');
    setMenu({
      anchor,
      testId: 'server-menu',
      items: [
        { id: 'invite', label: 'Leute einladen', icon: <UserPlus size={16} />, onSelect: () => overlays.open({ type: 'invite', serverId }), hidden: !can(perms, 'CREATE_INVITE') },
        { id: 'settings', label: 'Servereinstellungen', icon: <Settings size={16} />, onSelect: () => navigate(paths.serverSettings(serverId)), hidden: !canManageServer && !isOwner },
        { id: 'create-channel', label: 'Kanal erstellen', icon: <Plus size={16} />, onSelect: () => overlays.open({ type: 'createChannel', serverId }), hidden: !canManageChannels },
        { id: 'create-category', label: 'Kategorie erstellen', icon: <FolderPlus size={16} />, onSelect: () => overlays.open({ type: 'createChannel', serverId, defaultType: 'CATEGORY' }), hidden: !canManageChannels },
        { id: 'notifications', label: 'Benachrichtigungen', icon: <Bell size={16} />, onSelect: () => overlays.open({ type: 'notificationSettings', serverId }), separatorBefore: true },
        { id: 'profile', label: 'Serverprofil bearbeiten', icon: <Pencil size={16} />, onSelect: () => navigate(`${paths.settings('server-profil')}?server=${serverId}`) },
        {
          id: 'mark-read',
          label: 'Als gelesen markieren',
          onSelect: () => markServerRead(serverId),
        },
        ...extra.map((it) => ({
          id: `extra-${it.id}`,
          label: it.label,
          onSelect: () => (it.to ? navigate(it.to.replace(':serverId', serverId)) : overlays.open({ type: 'extra', item: it, ctx: { serverId } })),
        })),
        { id: 'leave', label: 'Server verlassen', icon: <LogOut size={16} />, danger: true, separatorBefore: true, onSelect: () => setConfirmLeave(true), hidden: isOwner },
      ],
    });
  };

  const channelMenu = (ch: ChannelDto, anchor: Anchor) => {
    const cperms = channelPermissions(useAppStore.getState(), ch.id);
    const chMuted = isMuted(useAppStore.getState(), 'CHANNEL', ch.id);
    setMenu({
      anchor,
      testId: 'channel-menu',
      items: [
        {
          id: 'mark-read',
          label: 'Als gelesen markieren',
          hidden: !ch.lastMessageId,
          onSelect: () => ch.lastMessageId && void channelsApi.ack(ch.id, ch.lastMessageId).then((rs) => useAppStore.getState().update((d) => void (d.readStates[ch.id] = rs))).catch(() => undefined),
        },
        {
          id: 'mute',
          label: chMuted ? 'Stummschaltung aufheben' : 'Kanal stummschalten',
          icon: chMuted ? <Bell size={16} /> : <BellOff size={16} />,
          onSelect: () =>
            void (chMuted ? notificationsApi.resetChannel(ch.id) : notificationsApi.setChannel(ch.id, { level: 'DEFAULT', muted: true }))
              .then((res) =>
                useAppStore.getState().update((d) => {
                  d.notificationSettings = d.notificationSettings.filter((x) => !(x.scopeType === 'CHANNEL' && x.scopeId === ch.id));
                  if (res) d.notificationSettings.push(res);
                }),
              )
              .catch((e) => toast.error('Fehler', errorMessage(e))),
        },
        { id: 'notifications', label: 'Benachrichtigungen …', onSelect: () => overlays.open({ type: 'notificationSettings', serverId, channelId: ch.id }) },
        { id: 'invite', label: 'Leute einladen', hidden: !can(cperms, 'CREATE_INVITE') || ch.type === 'CATEGORY', onSelect: () => overlays.open({ type: 'invite', serverId, channelId: ch.id }) },
        {
          id: 'copy-link',
          label: 'Link kopieren',
          hidden: ch.type === 'CATEGORY',
          onSelect: () => void navigator.clipboard?.writeText(window.location.origin + pathForChannel(ch)).then(() => toast.success('Link kopiert')),
        },
        {
          id: 'move-up',
          label: 'Nach oben',
          icon: <ArrowUp size={16} />,
          separatorBefore: true,
          hidden: !canManageChannels,
          disabled: !planStep(useAppStore.getState().channels, serverId, ch.id, -1),
          onSelect: () => commitPositions(planStep(useAppStore.getState().channels, serverId, ch.id, -1)),
        },
        {
          id: 'move-down',
          label: 'Nach unten',
          icon: <ArrowDown size={16} />,
          hidden: !canManageChannels,
          disabled: !planStep(useAppStore.getState().channels, serverId, ch.id, 1),
          onSelect: () => commitPositions(planStep(useAppStore.getState().channels, serverId, ch.id, 1)),
        },
        {
          id: 'move-to',
          label: 'In Kategorie verschieben …',
          icon: <FolderInput size={16} />,
          hidden: !canManageChannels || ch.type === 'CATEGORY',
          onSelect: () => moveToMenu(ch, anchor),
        },
        { id: 'edit', label: 'Kanal bearbeiten', icon: <Settings size={16} />, separatorBefore: true, hidden: !can(cperms, 'MANAGE_CHANNELS') && !can(cperms, 'MANAGE_ROLES'), onSelect: () => navigate(paths.channelSettings(ch.id)) },
        { id: 'create-in', label: 'Kanal in Kategorie erstellen', hidden: ch.type !== 'CATEGORY' || !canManageChannels, onSelect: () => overlays.open({ type: 'createChannel', serverId, parentId: ch.id }) },
        {
          id: 'delete',
          label: ch.type === 'CATEGORY' ? 'Kategorie löschen' : 'Kanal löschen',
          danger: true,
          hidden: !can(cperms, 'MANAGE_CHANNELS'),
          onSelect: () => navigate(paths.channelSettings(ch.id, 'loeschen')),
        },
      ],
    });
  };

  /** Neue Positionen optimistisch anwenden und speichern (bei Fehler zurückrollen). */
  const commitPositions = (body: PositionUpdate[] | null) => {
    if (!body || body.length === 0) return;
    const st = useAppStore.getState();
    const before = body.map((b) => {
      const c = st.channels[b.id]!;
      return { id: c.id, position: c.position, parentId: c.parentId, permissionsSynced: c.permissionsSynced };
    });
    st.update((d) => applyPositions(d.channels, body));
    channelsApi.positions(serverId, body).catch((e) => {
      useAppStore.getState().update((d) => {
        for (const o of before) {
          const c = d.channels[o.id];
          if (c) Object.assign(c, { position: o.position, parentId: o.parentId, permissionsSynced: o.permissionsSynced });
        }
      });
      toast.error('Sortieren fehlgeschlagen', errorMessage(e));
    });
  };

  /** Tastatur-Alternative: Ziel-Kategorie aus einem Menü wählen (Kanal wird ans Ende gesetzt). */
  const moveToMenu = (ch: ChannelDto, anchor: Anchor) => {
    const st = useAppStore.getState();
    const current = parentOf(st.channels, ch);
    const targets: { id: string | null; name: string }[] = [{ id: null, name: 'Ohne Kategorie' }, ...categoriesOf(st.channels, serverId).map((c) => ({ id: c.id, name: c.name ?? '' }))];
    setMenu({
      anchor,
      testId: 'channel-move-menu',
      items: targets.map((t) => ({
        id: t.id ?? 'none',
        label: t.name,
        disabled: t.id === current,
        onSelect: () => {
          const s2 = useAppStore.getState();
          commitPositions(planMove(s2.channels, serverId, ch.id, t.id, siblingsOf(s2.channels, serverId, t.id).length));
        },
      })),
    });
  };

  const startDrag = (e: React.DragEvent, id: string) => {
    if (!canManageChannels) return;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', id);
    setDragId(id);
  };
  const endDrag = () => {
    setDragId(null);
    setOver(null);
  };
  const dragOver = (e: React.DragEvent, target: ChannelDto) => {
    if (!dragId || dragId === target.id) return;
    const dragged = useAppStore.getState().channels[dragId];
    if (dragged?.type === 'CATEGORY' && target.type !== 'CATEGORY') return;
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    const after = target.type === 'CATEGORY' && dragged?.type !== 'CATEGORY' ? false : e.clientY > r.top + r.height / 2;
    if (over?.id !== target.id || over.after !== after) setOver({ id: target.id, after });
  };
  /** Drag & Drop: vor/nach der Zielzeile einsortieren, auf eine Kategorie = an deren Anfang. */
  const dropOn = (e: React.DragEvent, target: ChannelDto) => {
    e.preventDefault();
    if (dragId) commitPositions(planDrop(useAppStore.getState().channels, serverId, dragId, target.id, over?.id === target.id ? over.after : false));
    endDrag();
  };
  const dropUncategorized = (e: React.DragEvent) => {
    e.preventDefault();
    if (dragId) commitPositions(planMove(useAppStore.getState().channels, serverId, dragId, null, 0));
    endDrag();
  };
  const draggingChannel = !!dragId && useAppStore.getState().channels[dragId]?.type !== 'CATEGORY';
  const dropClass = (id: string) => (over?.id === id ? (over.after ? 'drop-after' : 'drop-before') : '');

  return (
    <div className="sidebar-inner" data-testid="server-sidebar">
      <header
        className={`sidebar-header server-header ${server.bannerUrl ? 'has-banner' : ''}`}
        style={server.bannerUrl ? { backgroundImage: `linear-gradient(to bottom, transparent, var(--lf-bg-1)), url("${server.bannerUrl}")` } : undefined}
      >
        <button className="server-header-button" onClick={(e) => serverMenu(anchorFrom(e.currentTarget))} aria-haspopup="menu" data-testid="server-header">
          <span className="truncate">{server.name}</span>
          {server.isMirrored && <span className="tag" title="Aus Discord gespiegelt">Brücke</span>}
          <ChevronDown size={18} />
        </button>
      </header>
      <div className={`channel-list scroll ${muted ? 'muted-server' : ''}`} role="tree" aria-label="Kanäle" data-testid="channel-list">
        {draggingChannel && groups.some((g) => g.category) && (
          <div className="channel-drop-zone" onDragOver={(e) => e.preventDefault()} onDrop={dropUncategorized} data-testid="channel-drop-uncategorized">
            Hierher ziehen: ohne Kategorie
          </div>
        )}
        {groups.map((g) => {
          const catId = g.category?.id ?? '__none';
          const isCollapsed = !!g.category && collapsed.has(catId);
          return (
            <div key={catId} className="channel-group" role="group">
              {g.category && (
                <div
                  className={`category-row ${dropClass(g.category.id)} ${dragId === g.category.id ? 'dragging' : ''}`}
                  draggable={canManageChannels}
                  onDragStart={(e) => startDrag(e, g.category!.id)}
                  onDragEnd={endDrag}
                  onDragOver={(e) => dragOver(e, g.category!)}
                  onDrop={(e) => dropOn(e, g.category!)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    channelMenu(g.category!, e.clientX || e.clientY ? { x: e.clientX, y: e.clientY } : anchorFrom(e.currentTarget));
                  }}
                  onKeyDown={(e) => {
                    if (canManageChannels && e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
                      e.preventDefault();
                      commitPositions(planStep(useAppStore.getState().channels, serverId, g.category!.id, e.key === 'ArrowUp' ? -1 : 1));
                    }
                  }}
                  data-testid="category"
                  data-channel-id={g.category.id}
                >
                  <button className="category-toggle" onClick={() => toggleCollapsed(catId)} aria-expanded={!isCollapsed} data-testid="category-toggle">
                    {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                    <span className="truncate">{g.category.name}</span>
                  </button>
                  {canManageChannels && (
                    <Tooltip label="Kanal erstellen">
                      <button className="icon-btn icon-btn-xs" onClick={() => overlays.open({ type: 'createChannel', serverId, parentId: g.category!.id })} data-testid="category-add-channel">
                        <Plus size={16} />
                      </button>
                    </Tooltip>
                  )}
                </div>
              )}
              {g.channels
                .filter((c) => !isCollapsed || c.id === activeId || (isUnread(useAppStore.getState(), c.id) && !isMuted(useAppStore.getState(), 'CHANNEL', c.id)))
                .map((c) => (
                  <ChannelRow
                    key={c.id}
                    channel={c}
                    active={c.id === activeId}
                    draggable={canManageChannels}
                    dropClass={`${dropClass(c.id)} ${dragId === c.id ? 'dragging' : ''}`}
                    onDragStart={(e) => startDrag(e, c.id)}
                    onDragEnd={endDrag}
                    onDragOver={(e) => dragOver(e, c)}
                    onDrop={(e) => dropOn(e, c)}
                    onStep={canManageChannels ? (dir) => commitPositions(planStep(useAppStore.getState().channels, serverId, c.id, dir)) : undefined}
                    onMenu={(a) => channelMenu(c, a)}
                    onEdit={() => navigate(paths.channelSettings(c.id))}
                    onOpen={() => {
                      void navigate(paths.channel(serverId, c.id));
                      useUi.getState().setNavOpen(false);
                    }}
                  />
                ))}
            </div>
          );
        })}
        {groups.length === 0 && <p className="empty small">Keine sichtbaren Kanäle.</p>}
      </div>
      {menu && <Menu anchor={menu.anchor} items={menu.items} onClose={() => setMenu(null)} testId={menu.testId} />}
      {confirmLeave && (
        <ConfirmDialog
          title={`„${server.name}" verlassen?`}
          message="Du kannst nur mit einer neuen Einladung wieder beitreten."
          confirmLabel="Server verlassen"
          danger
          onClose={() => setConfirmLeave(false)}
          onConfirm={() => {
            setConfirmLeave(false);
            membersApi
              .leave(serverId)
              .then(() => {
                useAppStore.getState().dispatch('SERVER_DELETE', { id: serverId, reason: 'left' });
                void navigate(paths.home());
              })
              .catch((e) => toast.error('Verlassen fehlgeschlagen', errorMessage(e)));
          }}
        />
      )}
    </div>
  );
}

interface RowProps {
  channel: ChannelDto;
  active: boolean;
  draggable: boolean;
  dropClass: string;
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: () => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  /** Tastatur-Sortierung (Alt + Pfeil hoch/runter). */
  onStep?: (dir: -1 | 1) => void;
  onMenu: (a: Anchor) => void;
  onOpen: () => void;
  onEdit: () => void;
}

function ChannelRow({ channel: c, active, draggable, dropClass, onDragStart, onDragEnd, onDragOver, onDrop, onStep, onMenu, onOpen, onEdit }: RowProps) {
  const unread = useAppStore((s) => isUnread(s, c.id));
  const mentions = useAppStore((s) => mentionCount(s, c.id));
  const muted = useAppStore((s) => isMuted(s, 'CHANNEL', c.id));
  const canManage = useAppStore((s) => can(channelPermissions(s, c.id), 'MANAGE_CHANNELS'));
  const voiceStates = useAppStore((s) => s.voiceStates);
  const users = useAppStore((s) => s.users);
  const inVoice = useMemo(() => (c.type === 'VOICE' ? Object.values(voiceStates).filter((v) => v.channelId === c.id) : []), [voiceStates, c.id, c.type]);
  const showUnread = unread && !muted && c.type !== 'VOICE';
  return (
    <div
      className={`channel-row-wrap ${active ? 'active' : ''} ${showUnread ? 'unread' : ''} ${muted ? 'muted' : ''} ${dropClass}`}
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onContextMenu={(e) => {
        e.preventDefault();
        onMenu(e.clientX || e.clientY ? { x: e.clientX, y: e.clientY } : anchorFrom(e.currentTarget));
      }}
    >
      <div className="channel-row" role="treeitem" aria-selected={active} data-testid="channel-item" data-channel-id={c.id} data-unread={showUnread ? 'true' : undefined}>
        {showUnread && <span className="unread-dot" aria-hidden="true" />}
        <button
          className="channel-link"
          onClick={onOpen}
          onKeyDown={(e) => {
            if (onStep && e.altKey && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
              e.preventDefault();
              onStep(e.key === 'ArrowUp' ? -1 : 1);
            }
          }}
          aria-current={active ? 'page' : undefined}
          aria-keyshortcuts={onStep ? 'Alt+ArrowUp Alt+ArrowDown' : undefined}
          data-testid="channel-link"
        >
          <ChannelIcon type={c.type} locked={c.permissionOverwrites.some((o) => o.targetType === 'ROLE' && o.targetId === c.serverId && (toBitfield(o.deny) & Permissions.VIEW_CHANNEL) !== 0n)} />
          <span className="truncate channel-name">{c.name}</span>
          {showUnread && <span className="sr-only">(ungelesen)</span>}
        </button>
        {mentions > 0 && (
          <span className="badge" data-testid="channel-mentions" aria-label={`${mentions} Erwähnungen`}>
            {mentions}
          </span>
        )}
        {canManage && (
          <span className="channel-actions">
            <Tooltip label="Kanal bearbeiten">
              <button className="icon-btn icon-btn-xs" onClick={onEdit} data-testid="channel-gear">
                <Settings size={14} />
              </button>
            </Tooltip>
          </span>
        )}
      </div>
      {inVoice.length > 0 && (
        <ul className="voice-members" data-testid="voice-members">
          {inVoice.map((v) => {
            const u = users[v.userId];
            return (
              <li key={v.userId} className="voice-member" data-testid="voice-member">
                <Avatar user={u} size={20} />
                <span className="truncate">{displayName(u)}</span>
                {(v.selfMute || v.serverMute) && <X size={12} aria-label="stumm" />}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
